import app from 'flarum/forum/app';
import Component from 'flarum/common/Component';
import TarotCardPicker from './TarotCardPicker';
import CityPicker from './CityPicker';

function parsePos(p) {
  if (typeof p === 'string') {
    try {
      p = JSON.parse(p);
    } catch (e) {
      p = [];
    }
  }
  return Array.isArray(p) ? p : [];
}

// 案例等级兜底（API /taro/case/bindings 失败时用）
// 字段格式：扁平结构，与 normalizeBinding() 输出一致
const FALLBACK_LEVELS = [
  { level: 'S1', id: 1, spreadIds: [] },
  { level: 'S2', id: 2, spreadIds: [] },
  { level: 'S3', id: 3, spreadIds: [] },
  { level: 'S4', id: 4, spreadIds: [] },
];

/**
 * 将 JSON:API 资源对象（含 attributes）归一化为扁平结构
 * 兼容两种输入：{ attributes: { level, spreadIds } } 或 { level, spreadIds }
 */
function normalizeBinding(b) {
  if (!b) return null;
  const a = b.attributes || b;
  return {
    id: b.id || a.id,
    level: a.level,
    spreadIds: Array.isArray(a.spreadIds) ? a.spreadIds : [],
    tagId: a.tagId || null,
    isActive: a.isActive !== false,
    sort: a.sort || 0,
  };
}

// 案例等级固定选项：S1~S4
const LEVEL_OPTIONS = ['S1', 'S2', 'S3', 'S4'];

export default class CaseComposer extends Component {
  oninit(vnode) {
    super.oninit(vnode);
    this.spreads = [];
    this.bindings = [];
    this.loading = true;
    this.submitting = false;
    this.selectedSpread = null;
    this.selectedLevel = null;
    this.selectedBinding = null;

    this.title = '';
    this.question = '';
    this.showDrawTime = true;
    this.drawTime = '';
    this.timezone = 'Asia/Shanghai';
    this.showDrawCity = true;
    this.drawCity = '';
    this.cityCountry = 'CN';
    this.cityProvince = 'CN-BJ';
    this.cityCityCode = 'BJ';
    this.cityCityName = '北京市';
    this.useReversed = true;
    this.cards = [];
    this.indicators = [];
    this.remark = '';
    this.spreadPositions = [];

    // 编辑模式：attrs.case 是已有的 taroCase 对象
    this.editingCaseId = null;
    this.isEditMode = false;
    if (this.attrs && this.attrs.case) {
      this.loadFromCase(this.attrs.case);
      this.isEditMode = true;
    }

    const user = app.session.user;
    // 管理组（大导师）和辅导员组都可以手动选择案例等级
    this.canManageLevel = !!(user && (user.isAdmin() || user.attribute('taroIsModerator')));
    this.userSwordLevel = user ? user.attribute('taroSwordLevel') || 'S0' : 'S0';

    // 先根据用户权限/等级同步设置默认值，避免异步加载前下拉框为空
    this.normalizeSelectedLevel();
    this.tryInitLevel();

    this.loadSpreads();
    this.loadBindings();
    // 非编辑模式才读用户偏好（编辑模式以原案例数据为准）
    if (!this.isEditMode) {
      this.loadUserPreferences();
    }
  }

  loadFromCase(taroCase) {
    this.editingCaseId = taroCase.id();
    const data = taroCase.caseData ? taroCase.caseData() : (taroCase.attribute('case_data') || {});
    this.title = data.title || '';
    this.question = data.question || '';
    this.selectedLevel = data.level || this.userSwordLevel;
    this.showDrawTime = !!data.drawTime;
    const rawDrawTime = data.drawTime || '';
    this.drawTime = rawDrawTime && !rawDrawTime.includes('T')
      ? rawDrawTime + 'T00:00'
      : rawDrawTime;
    this.timezone = data.drawTimezone || 'Asia/Shanghai';
    this.showDrawCity = !!data.drawCity;
    this.drawCity = data.drawCity || '';
    this.cityCountry = data.cityCountry || 'CN';
    this.cityProvince = data.cityProvince || 'CN-BJ';
    this.cityCityName = data.cityCityName || '北京市';
    this.useReversed = data.useReversed !== false;
    this.remark = data.remark || '';
    this.spreadId = data.spreadId || null;
    this.spreadCode = data.spreadCode || '';
    this.spreadName = data.spreadName || '';
    this.indicators = (data.indicatorCards || []).map((ind) => ({
      subject: ind.subject || '',
      cardName: ind.cardName || ind.name || '',
      cardCode: ind.code || '',
      isReversed: !!ind.isReversed,
    }));
    if (data.cards && data.cards.length > 0) {
      this.cards = data.cards.map((c) => ({
        position: c.position,
        posName: c.posName || c.name || '',
        cardName: c.name || c.cardName || '',
        cardCode: c.code || '',
        isReversed: !!c.is_reversed || !!c.isReversed,
      }));
    }
    // 同步 spreadPositions 用于 desc（编辑模式从 case_data 恢复）
    if (data.spreadPositions && data.spreadPositions.length > 0) {
      this.spreadPositions = data.spreadPositions.map((sp) => ({
        pos: sp.pos,
        name: sp.name,
        desc: sp.desc,
        adjustedName: sp.adjustedName || sp.name,
      }));
    }
  }

  loadUserPreferences() {
    const user = app.session.user;
    if (!user) return;
    const prefs = user.preferences ? user.preferences() : {};

    // 如果用户设置过默认时区/城市，使用之
    if (prefs.taroDefaultTimezone) {
      this.timezone = prefs.taroDefaultTimezone;
    }
    if (prefs.taroDefaultCity) {
      // prefs.taroDefaultCity 格式: "CN|CN-BJ|北京市" 或 "CN|CN-GD|广州市"
      const parts = String(prefs.taroDefaultCity).split('|');
      if (parts.length === 3) {
        this.cityCountry = parts[0];
        this.cityProvince = parts[1];
        this.cityCityName = parts[2];
      }
    }

    // 抽牌时间 = 当前时间（按用户选定时区计算，不是硬编码北京）
    this.drawTime = this.formatLocalTime(new Date(), this.timezone);
  }

  /**
   * 将 Date 对象按指定时区格式化为 YYYY-MM-DDTHH:MM（抽牌时间精确到分钟）
   */
  formatLocalTime(date, timezone) {
    try {
      const opts = { timeZone: timezone, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false };
      const parts = new Intl.DateTimeFormat('en-CA', opts).formatToParts(date);
      const get = (t) => parts.find(p => p.type === t)?.value || '';
      return `${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}`;
    } catch (e) {
      // 回退到 UTC+8
      const utc8 = new Date(date.getTime() + 8 * 60 * 60 * 1000);
      const yyyy = utc8.getUTCFullYear();
      const mm = String(utc8.getUTCMonth() + 1).padStart(2, '0');
      const dd = String(utc8.getUTCDate()).padStart(2, '0');
      const hh = String(utc8.getUTCHours()).padStart(2, '0');
      const mi = String(utc8.getUTCMinutes()).padStart(2, '0');
      return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
    }
  }

  loadSpreads() {
    app
      .request({ method: 'GET', url: app.forum.attribute('apiUrl') + '/taro/tarot/spreads' })
      .then((r) => {
        this.spreads = Array.isArray(r) ? r : r.data || [];
        // 编辑模式：牌阵列表加载后恢复已选牌阵，但不重置已恢复的阵位/牌数据
        if (this.isEditMode && this.spreadId) {
          const matched = this.spreads.find((s) => String(s.id) === String(this.spreadId));
          if (matched) {
            this.selectedSpread = matched;
            // 如果 loadFromCase 没有从 data.spreadPositions 恢复出 spreadPositions，从牌阵本身恢复
            if (this.spreadPositions.length === 0) {
              const attrs = matched.attributes || matched;
              const pos = parsePos(attrs.positions).filter((p) => p.type !== 'indicator');
              this.spreadPositions = pos.map((p) => ({
                pos: p.pos,
                name: p.name,
                desc: p.desc,
                adjustedName: p.name,
              }));
            }
          }
        }
        this.tryInitLevel();
        // 编辑模式：若原案例牌阵不在当前等级绑定允许名单，重置并提示
        if (
          this.isEditMode &&
          this.selectedSpread &&
          this.selectedBinding &&
          this.selectedBinding.spreadIds &&
          this.selectedBinding.spreadIds.length > 0 &&
          !this.isSpreadAllowed(this.selectedSpread)
        ) {
          this.selectedSpread = null;
          this.spreadId = null;
          this.spreadCode = '';
          this.spreadName = '';
          this.spreadPositions = [];
          this.cards = [];
          this.indicators = [];
          alert('原案例使用的牌阵已不适用于等级 ' + this.selectedLevel + '，请重新选择牌阵');
        }
        this.loading = this.bindingsLoading || false;
        m.redraw();
      })
      .catch(() => {
        this.loading = false;
        m.redraw();
      });
  }

  loadBindings() {
    this.bindingsLoading = true;
    app
      .request({ method: 'GET', url: app.forum.attribute('apiUrl') + '/taro/case/bindings' })
      .then((r) => {
        const raw = Array.isArray(r) ? r : r.data || [];
        // 归一化为扁平结构，后续统一用 .level / .spreadIds
        this.bindings = raw.length > 0 ? raw.map(normalizeBinding) : FALLBACK_LEVELS;
        // 如果等级已提前确定，刷新对应 binding 以应用牌阵限制
        if (this.selectedLevel) {
          this.selectedBinding = this.bindings.find((b) => b && b.level === this.selectedLevel) || null;
        }
        this.bindingsLoading = false;
        this.tryInitLevel();
        this.loading = false;
        m.redraw();
      })
      .catch(() => {
        // API 失败：使用兜底
        this.bindings = FALLBACK_LEVELS;
        if (this.selectedLevel) {
          this.selectedBinding = this.bindings.find((b) => b && b.level === this.selectedLevel) || null;
        }
        this.bindingsLoading = false;
        this.tryInitLevel();
        this.loading = false;
        m.redraw();
      });
  }

  normalizeSelectedLevel() {
    if (!this.selectedLevel) return;
    if (LEVEL_OPTIONS.includes(this.selectedLevel)) return;

    // 编辑旧数据时 level 不在 S1~S4，按权限回退
    this.selectedLevel = this.canManageLevel
      ? 'S2'
      : (LEVEL_OPTIONS.includes(this.userSwordLevel) ? this.userSwordLevel : 'S1');
    this.selectedBinding = this.bindings.find((b) => b && b.level === this.selectedLevel) || null;
  }

  tryInitLevel() {
    if (this.selectedLevel) return;

    if (this.canManageLevel) {
      // 管理组 / 辅导员默认 S2，可手动修改
      this.selectedLevel = 'S2';
      this.selectedBinding = this.bindings.find((b) => b && b.level === 'S2') || null;
      return;
    }

    // 普通学员：自动匹配当前宝剑积分对应的学员等级
    const userLevel = (this.userSwordLevel || 'S0').toUpperCase();
    let targetLevel = LEVEL_OPTIONS.find((lvl) => lvl === userLevel);
    if (!targetLevel) {
      const order = { S4: 4, S3: 3, S2: 2, S1: 1 };
      const userRank = order[userLevel] || 0;
      targetLevel =
        LEVEL_OPTIONS.slice()
          .sort((a, b) => order[b] - order[a])
          .find((lvl) => order[lvl] <= userRank) || 'S1';
    }
    this.selectedLevel = targetLevel;
    this.selectedBinding = this.bindings.find((b) => b && b.level === targetLevel) || null;
  }

  selectLevel(level, reset = true) {
    this.selectedLevel = level;
    this.selectedBinding = this.bindings.find((b) => b && b.level === level) || null;
    if (reset) {
      this.selectedSpread = null;
      this.cards = [];
      this.indicators = [];
      this.spreadPositions = [];
      m.redraw();
    }
  }

  getAllowedSpreads() {
    if (!this.selectedBinding || !this.selectedBinding.spreadIds || this.selectedBinding.spreadIds.length === 0) {
      return this.spreads;
    }
    const allowed = (this.selectedBinding.spreadIds || []).map(String);
    return this.spreads.filter((s) => allowed.indexOf(String(s.id)) >= 0);
  }

  selectSpread(spread) {
    // 强校验：牌阵必须属于当前等级绑定的允许范围
    if (!this.isSpreadAllowed(spread)) {
      alert('该牌阵不适用于当前案例等级（' + this.selectedLevel + '），请选择可用的牌阵');
      m.redraw();
      return;
    }
    this.selectedSpread = spread;
    const attrs = spread.attributes || spread;
    const pos = parsePos(attrs.positions).filter((p) => p.type !== 'indicator');
    this.spreadId = spread.id || null;
    this.spreadCode = attrs.code || '';
    this.spreadName = attrs.name || '';
    this.spreadPositions = pos.map((p) => ({
      pos: p.pos,
      name: p.name,
      desc: p.desc,
      adjustedName: p.name,
    }));
    this.cards = pos.map((p) => ({
      position: p.pos,
      posName: p.name,
      cardName: '',
      cardCode: '',
      isReversed: false,
    }));
    this.indicators = [];
    m.redraw();
  }

  isSpreadAllowed(spread) {
    if (!spread) return false;
    // 无绑定或绑定放开全部牌阵时，允许所有
    if (!this.selectedBinding || !this.selectedBinding.spreadIds || this.selectedBinding.spreadIds.length === 0) {
      return true;
    }
    const allowed = (this.selectedBinding.spreadIds || []).map(String);
    return allowed.indexOf(String(spread.id)) >= 0;
  }

  resetSpread() {
    this.selectedSpread = null;
    this.spreadId = null;
    this.spreadCode = '';
    this.spreadName = '';
    this.cards = [];
    this.indicators = [];
    this.spreadPositions = [];
    m.redraw();
  }

  addIndicator() {
    if (this.indicators.length < 4) {
      this.indicators.push({ subject: '', cardName: '', cardCode: '', isReversed: false });
      m.redraw();
    }
  }

  removeIndicator(i) {
    this.indicators.splice(i, 1);
    m.redraw();
  }

  getUsedCardCodes(excludeCardIdx, excludeIndicatorIdx) {
    const codes = [];
    this.cards.forEach((c, idx) => {
      if (idx !== excludeCardIdx && c.cardCode) codes.push(c.cardCode);
    });
    this.indicators.forEach((ind, idx) => {
      if (idx !== excludeIndicatorIdx && ind.cardCode) codes.push(ind.cardCode);
    });
    return codes;
  }

  submit() {
    if (!this.selectedLevel) {
      alert('请先选择案例等级');
      return;
    }
    if (!this.title) {
      alert('请填写问题标题');
      return;
    }
    if (!this.selectedSpread) {
      alert('请选择牌阵');
      return;
    }
    // 再次校验：牌阵必须属于当前等级绑定的允许范围
    if (!this.isSpreadAllowed(this.selectedSpread)) {
      alert('当前牌阵不适用于等级 ' + this.selectedLevel + '，请重新选择');
      return;
    }
    if (this.cards.some((c) => !c.cardName)) {
      alert('请填写所有牌位');
      return;
    }
    if (this.showDrawTime && this.drawTime) {
      const ok = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?$/.test(this.drawTime);
      if (!ok) {
        alert('抽牌时间格式不正确，请重新选择日期与时间。\n当前值：' + this.drawTime);
        return;
      }
    }

    const allCodes = [
      ...this.cards.map((c) => c.cardCode),
      ...this.indicators.map((ind) => ind.cardCode),
    ].filter(Boolean);
    if (new Set(allCodes).size !== allCodes.length) {
      alert('存在重复的塔罗牌，请确保每个位置选择不同的牌');
      return;
    }

    this.submitting = true;

    const cardsData = this.cards.map((c) => ({
      name: c.cardName,
      position: c.position,
      posName: c.posName,
      is_reversed: this.useReversed ? c.isReversed : false,
    }));

    const indicatorData = this.indicators.map((ind) => ({
      subject: ind.subject || '',
      name: ind.cardName || '',
      cardName: ind.cardName || '',
      is_reversed: this.useReversed ? !!ind.isReversed : false,
      isReversed: this.useReversed ? !!ind.isReversed : false,
    }));

    const spreadAttrs = this.selectedSpread.attributes || this.selectedSpread;
    const originalPositions = parsePos(spreadAttrs.positions).filter((p) => p.type !== 'indicator');

    const binding = this.selectedBinding || {};

    const caseData = {
      title: this.title,
      question: this.question,
      level: this.selectedLevel,
      drawTime: this.showDrawTime ? this.drawTime : '',
      drawTimezone: this.showDrawTime ? this.timezone : '',
      drawCity: this.showDrawCity ? this.cityCityName : '',
      cityCountry: this.showDrawCity ? this.cityCountry : '',
      cityProvince: this.showDrawCity ? this.cityProvince : '',
      cityCityName: this.showDrawCity ? this.cityCityName : '',
      useReversed: this.useReversed,
      remark: this.remark,
      spreadId: this.spreadId,
      spreadCode: this.spreadCode,
      spreadName: this.spreadName,
      spreadPositions: this.spreadPositions,
      indicatorCards: indicatorData,
      cards: cardsData,
    };

    const tarotParams = {
      level: this.selectedLevel,
      spread_id: this.spreadId,
      spread_code: this.spreadCode,
      spread_name: this.spreadName,
      positions: originalPositions,
      spread_positions: this.spreadPositions,
      indicator_cards: indicatorData,
      cards: cardsData,
      question: this.question,
      use_reversed: this.useReversed,
      engine_url: binding.engineUrl || '',
      engine_code: binding.engineCode || '',
      dify_chatflow_id: binding.difyChatflowId || '',
    };

    const method = this.editingCaseId ? 'PATCH' : 'POST';
    const url = this.editingCaseId
      ? `${app.forum.attribute('apiUrl')}/taro/cases/${this.editingCaseId}`
      : `${app.forum.attribute('apiUrl')}/taro/cases`;

    app
      .request({
        method: method,
        url: url,
        body: {
          data: {
            type: 'taro-cases',
            attributes: {
              title: this.title,
              content: this.formatPostContent(),
              case_data: caseData,
              tarot_params: tarotParams,
              // 顶层也发送 spread_id，确保 updateCase 时能正确更新
              spread_id: this.spreadId,
            },
          },
        },
      })
      .then((result) => {
        const data = result.data;
        if (data) {
          const attrs = (data.attributes) || {};
          const discussionId = attrs.discussionId;
          if (!discussionId) {
            // 响应异常：缺 discussionId，无法安全导航，提示用户手动跳转
            this.submitting = false;
            alert('发布成功，但响应缺少 discussionId，请返回首页查看');
            m.redraw();
            return;
          }
          // 案例发布/编辑成功后，服务器已自动生成牌阵图。
          // 若 API 返回了 spreadImageUrl，追加到首贴内容（如果首贴还没有图片）。
          const imageUrl = attrs.spreadImageUrl;
          if (imageUrl) {
            const discussion = app.store.getById('discussions', discussionId);
            if (discussion && discussion.firstPost && discussion.firstPost()) {
              const firstPost = discussion.firstPost();
              if (firstPost && firstPost.content && !firstPost.content.includes(imageUrl.split('?')[0])) {
                firstPost.pushAttributes({
                  content: '![](' + imageUrl + ')\n\n' + firstPost.content
                });
                firstPost.save();
              }
            }
          }
          if (discussionId) {
            m.route.set('/d/' + discussionId);
          }
          // 页面模式下无浮层，跳回首页；浮层模式下隐藏 composer
          if (app.composer && typeof app.composer.isActive === 'function' && app.composer.isActive()) {
            app.composer.hide();
          } else {
            m.route.set('/');
          }
          // 编辑成功后强制刷新页面，确保主贴标题与正文实时更新
          if (this.isEditMode) {
            window.location.reload();
          }
        }
      })
      .catch((err) => {
        this.submitting = false;
        // Flarum JSON:API 错误格式: err.response.data.errors[0].detail
        let msg = '错误';
        try {
          const errors = err.response && err.response.data && err.response.data.errors;
          if (errors && errors.length > 0) {
            msg = errors.map(function (e) { return e.detail || ''; }).filter(Boolean).join('；') || errors[0].status || '错误';
          }
        } catch (_) { /* fallthrough */ }
        alert('发布失败: ' + msg);
        m.redraw();
      });
  }

  formatPostContent() {
    let content = '';
    if (this.question) content += '【问题背景】\n' + this.question + '\n\n';
    // 指示牌按塔罗阅读习惯放在牌阵内容之前
    if (this.indicators.length > 0) {
      content += '【指示牌】\n';
      this.indicators.forEach((ind) => {
        const rev = this.useReversed && ind.isReversed ? '（逆位）' : '';
        content += '- ' + (ind.subject || '') + (ind.subject && ind.cardName ? '：' : '') + ind.cardName + rev + '\n';
      });
      content += '\n';
    }
    content +=
      '【牌阵】' +
      (this.selectedSpread
        ? this.selectedSpread.attributes
          ? this.selectedSpread.attributes.name
          : this.selectedSpread.name
        : '') +
      '\n\n';
    this.cards.forEach((c) => {
      const rev = this.useReversed && c.isReversed ? '（逆位）' : '';
      content += '- ' + c.posName + '：' + c.cardName + rev + '\n';
    });
    if (this.showDrawTime && this.drawTime) {
      content += '\n抽牌时间：' + this.drawTime;
      if (this.timezone) content += ' (' + this.timezone + ')';
    }
    if (this.showDrawCity && this.cityCityName) {
      const cityParts = [this.cityCityName];
      if (this.cityProvince) cityParts.unshift(this.cityProvince);
      if (this.cityCountry) cityParts.unshift(this.cityCountry);
      content += '\n抽牌城市：' + cityParts.join(' / ');
    }
    if (this.remark) content += '\n\n【补充】\n' + this.remark;
    return content;
  }

  view() {
    if (this.loading) {
      return <div style="padding:20px;">加载牌阵中...</div>;
    }

    const hasIndicator =
      this.selectedSpread &&
      (this.selectedSpread.attributes
        ? this.selectedSpread.attributes.hasIndicator
        : this.selectedSpread.hasIndicator);

    return (
      <div className="TaroCaseForm">
        <div className="TaroCaseForm-header" style="display:flex;align-items:center;justify-content:space-between;">
          <h3 style="margin:0;color:#4A90D9;">案例编辑</h3>
          <div>
            <button className="Button" style="margin-right:8px;" onclick={() => {
              if (app.composer && typeof app.composer.isActive === 'function' && app.composer.isActive()) {
                app.composer.hide();
              } else {
                m.route.set('/');
              }
            }}>取消</button>
            <button className="Button Button--primary" disabled={this.submitting} onclick={() => this.submit()}>
              {this.submitting ? '发布中...' : '发布'}
            </button>
          </div>
        </div>

        <div className="TaroCaseForm-body">
          <div className="Form-group">
            <label>案例等级</label>
            {this.canManageLevel ? (
              <select
                className="FormControl"
                style="width:150px;"
                value={this.selectedLevel || ''}
                onchange={(e) => this.selectLevel(e.target.value)}
              >
                {LEVEL_OPTIONS.map((lvl) => (
                  <option value={lvl}>{lvl}</option>
                ))}
              </select>
            ) : (
              <div className="FormControl TaroLevelReadonly">
                <span className="TaroLevelValue">{this.selectedLevel || '加载中...'}</span>
                <span className="TaroLevelHint">（根据您的宝剑积分自动设置）</span>
              </div>
            )}
          </div>

          {this.selectedLevel ? (
            <div className="TaroCaseForm-fields">
          <div className="Form-group">
            <label>一句话问题</label>
            <input
              className="FormControl"
              placeholder="用一句话概括你的问题..."
              value={this.title}
              oninput={(e) => (this.title = e.target.value)}
            />
          </div>

          <div className="Form-group">
            <label>问题背景描述</label>
            <textarea
              className="FormControl"
              placeholder="详细描述你的问题或背景情况..."
              value={this.question}
              oninput={(e) => (this.question = e.target.value)}
              rows={3}
            />
          </div>

          <div className="Form-group">
            <label>抽牌时间</label>
            <div className="TaroSwitchRow">
              <label className="TaroSwitch">
                <input
                  type="checkbox"
                  checked={this.showDrawTime}
                  onchange={(e) => (this.showDrawTime = e.target.checked)}
                />
                <span className="TaroSlider"></span>
              </label>
              {this.showDrawTime ? (
                <input
                  className="FormControl"
                  type="datetime-local"
                  style="max-width:220px;"
                  value={this.drawTime}
                  oninput={(e) => (this.drawTime = e.target.value)}
                />
              ) : null}
              <select
                className="FormControl TaroTimezoneSelect"
                style="max-width:220px;"
                value={this.timezone}
                onchange={(e) => (this.timezone = e.target.value)}
                disabled={!this.showDrawTime}
              >
                <option value="Asia/Shanghai">东八区 (UTC+8) 北京/上海/香港</option>
                <option value="Asia/Tokyo">东九区 (UTC+9) 东京</option>
                <option value="Asia/Singapore">东八区 (UTC+8) 新加坡</option>
                <option value="Asia/Seoul">东九区 (UTC+9) 首尔</option>
                <option value="Asia/Taipei">东八区 (UTC+8) 台北</option>
                <option value="Asia/Hong_Kong">东八区 (UTC+8) 香港</option>
                <option value="Asia/Urumqi">东六区 (UTC+6) 乌鲁木齐</option>
                <option value="Asia/Kolkata">东五点半 (UTC+5:30) 孟买</option>
                <option value="Asia/Bangkok">东七区 (UTC+7) 曼谷</option>
                <option value="Europe/London">零时区 (UTC+0) 伦敦</option>
                <option value="Europe/Paris">东一区 (UTC+1) 巴黎</option>
                <option value="Europe/Berlin">东一区 (UTC+1) 柏林</option>
                <option value="Europe/Moscow">东三区 (UTC+3) 莫斯科</option>
                <option value="America/New_York">西五区 (UTC-5) 纽约</option>
                <option value="America/Chicago">西六区 (UTC-6) 芝加哥</option>
                <option value="America/Denver">西七区 (UTC-7) 丹佛</option>
                <option value="America/Los_Angeles">西八区 (UTC-8) 洛杉矶</option>
                <option value="America/Toronto">西五区 (UTC-5) 多伦多</option>
                <option value="America/Vancouver">西八区 (UTC-8) 温哥华</option>
                <option value="America/Sao_Paulo">西三区 (UTC-3) 圣保罗</option>
                <option value="America/Buenos_Aires">西三区 (UTC-3) 布宜诺斯艾利斯</option>
                <option value="Australia/Sydney">东十区 (UTC+10) 悉尼</option>
                <option value="Australia/Melbourne">东十区 (UTC+10) 墨尔本</option>
                <option value="Australia/Perth">东八区 (UTC+8) 珀斯</option>
                <option value="Pacific/Auckland">东十二区 (UTC+12) 奥克兰</option>
                <option value="Pacific/Honolulu">西十区 (UTC-10) 檀香山</option>
                <option value="Africa/Cairo">东二区 (UTC+2) 开罗</option>
                <option value="Africa/Johannesburg">东二区 (UTC+2) 约翰内斯堡</option>
                <option value="UTC">协调世界时 (UTC+0)</option>
              </select>
            </div>
          </div>

          <div className="Form-group">
            <label>抽牌城市</label>
            <div className="TaroSwitchRow">
              <label className="TaroSwitch">
                <input
                  type="checkbox"
                  checked={this.showDrawCity}
                  onchange={(e) => (this.showDrawCity = e.target.checked)}
                />
                <span className="TaroSlider"></span>
              </label>
              {this.showDrawCity ? (
                <span style="font-size:13px;color:#555;">
                  {this.cityCountry || 'CN'} · {this.cityProvince || 'CN-BJ'} · {this.cityCityName || '北京市'}
                  <span style="color:#999;font-size:11px;margin-left:6px;">（点击下方"选择城市"修改）</span>
                </span>
              ) : null}
            </div>
            {this.showDrawCity ? (
              <CityPicker
                country={this.cityCountry}
                province={this.cityProvince}
                city={this.cityCityName}
                onChange={(c, p, ci, tz) => {
                  this.cityCountry = c;
                  this.cityProvince = p;
                  this.cityCityName = ci;
                  if (tz) this.timezone = tz;
                }}
              />
            ) : null}
          </div>

          <div className="Form-group">
            <label>选择牌阵
              {this.selectedBinding && this.selectedBinding.spreadIds && this.selectedBinding.spreadIds.length > 0 ? (
                <span style={{ marginLeft: '8px', fontSize: '12px', color: '#999', fontWeight: 'normal' }}>
                  （仅显示 {this.selectedLevel} 等级可用牌阵）
                </span>
              ) : null}
            </label>
            {!this.selectedSpread ? (
              <select
                className="FormControl"
                onchange={(e) => {
                  const s = this.getAllowedSpreads().find(
                    (s) => String(s.id) === String(e.target.value)
                  );
                  if (s) this.selectSpread(s);
                }}
              >
                <option value="">-- 请选择牌阵 --</option>
                {this.getAllowedSpreads().map((s) => {
                  const attrs = s.attributes || s;
                  return (
                    <option value={s.id}>
                      {attrs.name}（{attrs.description || '无分类'}，
                      {parsePos(attrs.positions).filter((p) => p.type !== 'indicator').length}张）
                    </option>
                  );
                })}
              </select>
            ) : (
              <div>
                <strong>
                  {this.selectedSpread.attributes
                    ? this.selectedSpread.attributes.name
                    : this.selectedSpread.name}
                </strong>{' '}
                <button className="Button Button--link" onclick={() => this.resetSpread()}>
                  [更换]
                </button>
                {(this.selectedSpread.attributes
                  ? this.selectedSpread.attributes.tutorialLink
                  : this.selectedSpread.tutorialLink) ? (
                  <a
                    className="Button Button--link Button--small"
                    href={
                      this.selectedSpread.attributes
                        ? this.selectedSpread.attributes.tutorialLink
                        : this.selectedSpread.tutorialLink
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    学习
                  </a>
                ) : null}
              </div>
            )}
          </div>

          {this.selectedSpread && hasIndicator ? (
            <div className="Form-group">
              <label>使用逆位（总开关）</label>
              <div className="TaroSwitchRow">
                <label className="TaroSwitch">
                  <input
                    type="checkbox"
                    checked={this.useReversed}
                    onchange={(e) => (this.useReversed = e.target.checked)}
                  />
                  <span className="TaroSlider"></span>
                </label>
                <span style="margin-left:10px;font-size:12px;color:#666;">
                  关闭后所有牌默认为正位
                </span>
              </div>
            </div>
          ) : null}

          {this.selectedSpread && hasIndicator ? (
            <div className="Form-group TaroIndicatorSection">
              <label>指示牌（0~4个，可选）</label>
              <p className="TaroFieldHint">指代对象表示这张牌代表什么，可以不填</p>
              {this.indicators.map((ind, i) => (
                <div className="TaroPositionRow" key={i}>
                  <input
                    className="FormControl"
                    placeholder="指代对象"
                    value={ind.subject}
                    oninput={(e) => (ind.subject = e.target.value)}
                  />
                  <TarotCardPicker
                    selected={ind.cardName}
                    selectedCode={ind.cardCode}
                    allowReversed={this.useReversed}
                    isReversed={!!ind.isReversed}
                    disabledCodes={this.getUsedCardCodes(-1, i)}
                    onSelect={(name, code) => {
                      ind.cardName = name;
                      ind.cardCode = code;
                    }}
                    onToggleReversed={() => {
                      ind.isReversed = !ind.isReversed;
                    }}
                  />
                  {this.useReversed ? (
                    <button
                      className={'TaroReverse ' + (ind.isReversed ? 'is-on' : '')}
                      onclick={() => {
                        ind.isReversed = !ind.isReversed;
                      }}
                      type="button"
                      title="逆位"
                    >
                      R
                    </button>
                  ) : null}
                  <button
                    className="Button Button--danger"
                    style="padding:5px 8px;"
                    onclick={() => this.removeIndicator(i)}
                  >
                    ✕
                  </button>
                </div>
              ))}
              {this.indicators.length < 4 ? (
                <button className="Button" onclick={() => this.addIndicator()}>
                  + 添加指示牌
                </button>
              ) : null}
            </div>
          ) : null}

          {this.selectedSpread ? (
            <div className="Form-group">
              <label>阵位设置</label>
              {this.spreadPositions.map((sp, idx) => (
                <div className="Form-group" key={idx}>
                  <div className="TaroPositionRow">
                    <span className="TaroPosNumber">{sp.pos}.</span>
                    <input
                      className="FormControl"
                      placeholder="阵位名称"
                      value={sp.adjustedName}
                      oninput={(e) => {
                        sp.adjustedName = e.target.value;
                        this.cards[idx].posName = e.target.value;
                      }}
                    />
                    <TarotCardPicker
                      selected={this.cards[idx].cardName}
                      selectedCode={this.cards[idx].cardCode}
                      allowReversed={this.useReversed}
                      isReversed={this.cards[idx].isReversed}
                      disabledCodes={this.getUsedCardCodes(idx, -1)}
                      onSelect={(name, code) => {
                        this.cards[idx].cardName = name;
                        this.cards[idx].cardCode = code;
                      }}
                      onToggleReversed={() => {
                        this.cards[idx].isReversed = !this.cards[idx].isReversed;
                      }}
                    />
                    {this.useReversed ? (
                      <button
                        className={'TaroReverse ' + (this.cards[idx].isReversed ? 'is-on' : '')}
                        onclick={() => {
                          this.cards[idx].isReversed = !this.cards[idx].isReversed;
                        }}
                        type="button"
                        title="逆位"
                      >
                        R
                      </button>
                    ) : null}
                  </div>
                  {sp.desc ? <div className="TaroPositionDesc">{sp.desc}</div> : null}
                </div>
              ))}
            </div>
          ) : null}

          <div className="Form-group">
            <label>其他补充（可选）</label>
            <textarea
              className="FormControl"
              rows={2}
              placeholder="其他需要补充的信息..."
              value={this.remark}
              oninput={(e) => (this.remark = e.target.value)}
            />
          </div>
            </div>
          ) : (
            <div className="Form-group">
              <p className="TaroFieldHint">请先确认上方“案例等级”后再填写后续内容。</p>
            </div>
          )}
        </div>


      </div>
    );
  }
}
